<template>
  <b-card no-body>
    <b-row>
      <b-col cols="8">
        <b-card-header>
          <b-card-title>
            {{ document.question }} -
            {{ $t("questionnaire.questionType.multipleChoice") }}
          </b-card-title>
        </b-card-header>
        <b-card-body>
          <b-card-text
            v-for="(content, index) in document.content"
            :key="index"
          >
            <b-card-text class="d-flex justify-content-between">
              <span>{{ content.label }}</span>
              <span>{{ getAnswerPercents(content) }}%</span>
            </b-card-text>
            <b-card-text>
              <b-progress
                :value="getAnswerPercents(content)"
                max="100"
                height="6px"
                variant="primary"
              />
            </b-card-text>
            <b-card-text class="d-flex justify-content-between align-items-center">
              <b-avatar-group
                v-if="content.users.length > 1"
                size="32px"
              >
                <b-avatar
                  v-for="user in content.users.slice(0, MAX_AVATAR_NUMBER)"
                  :key="user.id"
                  variant="light-primary"
                  :text="getInitials(user.fullName)"
                />
              </b-avatar-group>
              <b-avatar
                v-else-if="content.users.length == 1"
                variant="light-primary"
                :text="getInitials(content.users[0].fullName)"
              />
              <div v-else />
              <b-button
                variant="flat-default pr-0"
                @click="handleShowUserVotes(content.users)"
              >
                <span
                  class="align-middle"
                >
                  {{ content.users.length }} {{ $t('questionnaire.votes') }}
                </span>
                <feather-icon
                  icon="ChevronRightIcon"
                  class="ml-50"
                />
              </b-button>
            </b-card-text>
          </b-card-text>
        </b-card-body>
      </b-col>
      <b-col
        cols="4"
        style="border-left: 1px solid #ebe9f1"
      >
        <b-card-body>
          <vue-apex-charts
            type="donut"
            height="350"
            :options="chartOptions"
            :series="series"
          />
        </b-card-body>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BAvatar,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BProgress,
  BAvatarGroup,
  BButton,
  VBModal,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { COLORS } from '@/shared/constants'
import { getInitials } from '@hw-core/utils'

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BRow,
    BCol,
    BAvatar,
    BProgress,
    BAvatarGroup,
    BButton,

    VueApexCharts,
    BCardBody,
  },
  props: {
    document: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      MAX_AVATAR_NUMBER: 5,
    }
  },
  computed: {
    series() {
      return this.document.content.map(content => content.users.length)
    },
    chartOptions() {
      const colors = Object.values(COLORS)

      return {
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '14px',
          fontFamily: 'Montserrat',
        },
        colors: colors.splice(
          0,
          this.document.content.map(({ label }) => label).length,
        ),
        dataLabels: {
          enabled: true,
          formatter: val => `${Math.round(val)}%`,
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                name: {
                  fontSize: '2rem',
                  fontFamily: 'Montserrat',
                },
                value: {
                  fontSize: '1rem',
                  fontFamily: 'Montserrat',
                  formatter: val => `${Math.round((val / this.totalAnswers) * 100)}%`,
                },
                total: {
                  show: true,
                  fontSize: '1.5rem',
                  label: 'Answers',
                  formatter() {
                    return this.totalAnswers
                  },
                },
              },
            },
          },
        },
        labels: this.document.content.map(({ label }) => label),
        responsive: [
          {
            breakpoint: 992,
            options: {
              chart: {
                height: 380,
              },
              legend: {
                position: 'bottom',
              },
            },
          },
          {
            breakpoint: 576,
            options: {
              chart: {
                height: 320,
              },
              plotOptions: {
                pie: {
                  donut: {
                    labels: {
                      show: true,
                      name: {
                        fontSize: '1.5rem',
                      },
                      value: {
                        fontSize: '1rem',
                      },
                      total: {
                        fontSize: '1.5rem',
                      },
                    },
                  },
                },
              },
              legend: {
                show: true,
              },
            },
          },
        ],
      }
    },
    totalAnswers() {
      return this.document.content.reduce(
        // eslint-disable-next-line no-return-assign, no-param-reassign
        (total, content) => (total += content.users.length),
        0,
      )
    },
  },
  methods: {
    getInitials,
    getAnswerPercents(content) {
      return content.users.length > 0 ? Math.round((content.users.length / this.totalAnswers) * 100) : 0
    },
    handleShowUserVotes(users) {
      this.$emit('show-vote-list', users)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep div {
  .apexcharts-canvas {
    .apexcharts-legend {
      justify-content: space-around;
    }
  }
}
</style>
